<template>
    <app-layout>
        <page-header :title="title" />
        <row>
            <column md="12" xl="6">
                <jet-form-section type="decent" @submitted="handleSubmit($event)">
                    <template #form>
                        <jet-input
                            v-model="form.firstName"
                            name="first_name"
                            :autofocus="true"
                            :invalidFeedback="form.errors.firstName"
                            :label="trans('users.edit_user.field.first_name.label')"
                            :readonly="!isEditable"
                            :required="true"
                        />
                        <jet-input
                            v-model="form.lastName"
                            name="last_name"
                            :invalidFeedback="form.errors.lastName"
                            :label="trans('users.edit_user.field.last_name.label')"
                            :readonly="!isEditable"
                            :required="true"
                            icon="cil-qr-code"
                        />
                        <jet-input
                            v-model="form.externalUserId"
                            name="external_user_id"
                            :invalidFeedback="form.errors.externalUserId"
                            :label="trans('users.edit_user.field.external_user_id.label')"
                            :placeholder="trans('users.edit_user.field.external_user_id.placeholder')"
                            :readonly="!isEditable"
                        />
                        <jet-input
                            v-model="form.email"
                            name="email"
                            :invalidFeedback="form.errors.email"
                            :label="trans('users.edit_user.field.email.label')"
                            :readonly="!isEditable"
                            :required="true"
                        />
                        <jet-select
                            v-if="availableRoles.length > 0"
                            v-model="form.role"
                            name="role"
                            :invalidFeedback="form.errors.role"
                            :label="trans('users.edit_user.field.role.label')"
                            :options="availableRoles"
                            :disabled="!canChangeRole"
                            :readonly="!isEditable"
                            :required="true"
                        />
                        <hierarchical-multi-select
                            v-if="hasAnyPermission('location.assign_user')"
                            v-model="form.locations"
                            name="locations"
                            :disabled="!isEditable"
                            :invalid-feedback="form.errors.locations"
                            :is-form-field="true"
                            :label="trans('users.edit_user.field.locations.label')"
                            :hierarchical-options="hierarchicalLocations"
                            :placeholder="trans('users.edit_user.field.locations.placeholder')"
                            :required="true"
                            :show-searchbar="true"
                            :show-select-all-button="false"
                        />
                        <jet-select
                            v-model="form.language"
                            name="language"
                            :invalidFeedback="form.errors.language"
                            :label="trans('users.edit_user.field.language.label')"
                            :options="supportedLanguages"
                            :resettable="false"
                        />
                    </template>

                    <template #actions>
                        <jet-form-buttons
                            :cancel-route="$route('users.index')"
                            :disabled="!form.isDirty"
                            :has-changes="form.isDirty"
                            :is-processing="form.processing"
                            :is-update-form="isUpdateForm"
                            :primary-action-label="primaryActionLabel"
                            @submit="handleSubmit"
                        />
                    </template>
                </jet-form-section>
            </column>
        </row>
    </app-layout>
</template>
<script>
import AppLayout from '@/Layouts/AppLayout.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetSelect from '@/Jetstream/Select.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetFormButtons from '@/Jetstream/FormButtons.vue';
import { getLabelForLocale } from '@/Utils/getLabelForLocale';
import HierarchicalMultiSelect from '@/Components/HierarchicalMultiSelect.vue';
import PageHeader from '@/Components/PageHeader.vue';

export default {
    components: {
        PageHeader,
        HierarchicalMultiSelect,
        AppLayout,
        JetFormButtons,
        JetInput,
        JetFormSection,
        JetSelect,
    },

    data() {
        return {
            form: this.$inertia.form({
                firstName: '',
                lastName: '',
                externalUserId: '',
                email: '',
                role: '3',
                language: null,
                locations: [],
            }),
        };
    },

    computed: {
        title() {
            return this.isUpdateForm ? this.trans('users.edit_user.title', { name: this.fullName }) : this.trans('users.create_user.title');
        },
        user() {
            return this.$page.props.user;
        },
        fullName() {
            return this.user.name;
        },
        isUpdateForm() {
            return !!this.user;
        },
        isEditable() {
            return !this.isUpdateForm || this.$page.props.editable;
        },
        availableRoles() {
            return this.$page.props.assignableRoles.data.map((role) => ({
                value: role.id.toString(),
                label: role.name.toUpperCase(),
            }));
        },
        canChangeRole() {
            return this.hasAnyPermission('role.update');
        },
        defaultUserRole() {
            return this.$page.props.defaultUserRole;
        },
        defaultLanguage() {
            return this.$page.props.locale;
        },
        supportedLanguages() {
            return this.$page.props.supported_locales.map((locale) => ({
                value: locale,
                label: getLabelForLocale(locale),
            }));
        },
        hierarchicalLocations() {
            return this.$page.props.locations || [];
        },
        primaryActionLabel() {
            return this.isUpdateForm ? this.trans('general.action.save') : this.trans('general.action.add');
        },
    },

    mounted() {
        if (this.isUpdateForm) {
            this.form = this.$inertia.form({
                firstName: this.user.firstName,
                lastName: this.user.lastName,
                externalUserId: this.user.externalUserId ?? '',
                email: this.user.email,
                role: this.user.roles.at(0)?.id.toString(),
                language: this.user.language || this.defaultLanguage,
                locations: (this.user.locations || []).map((loc) => loc.id),
            });
        } else {
            this.form.language = this.defaultLanguage;
            this.form.role = this.defaultUserRole.id.toString();
        }
    },

    methods: {
        handleSubmit(event) {
            if (this.isUpdateForm) {
                this.updateUser();
            } else {
                this.createUser();
            }
        },
        createUser() {
            this.form.transform(this.transformValues).post(route('users.store'));
        },
        updateUser() {
            this.form.transform(this.transformValues).put(route('users.update', { user: this.user }));
        },
        transformValues(data) {
            const locations = Array.isArray(data.locations) ? data.locations : data.locations ? [data.locations] : [];

            return {
                ...data,
                role: parseInt(data.role),
                locations: this.computeCompressedLocations(this.hierarchicalLocations, locations),
            };
        },
        // Compress selection to top-most fully covered nodes
        computeCompressedLocations(hierarchicalRoots, selectedIds) {
            const selectedSet = new Set(selectedIds.map(this.coerceToNumber));
            const selectedLeafSet = new Set(this.getAllLeavesFromRoots(hierarchicalRoots).filter((id) => selectedSet.has(id)));

            const outputs = (hierarchicalRoots || []).flatMap((root) => this.collectTopMostCovered(root, selectedLeafSet).outputs);

            return [...new Set(outputs)].map(this.coerceToNumber);
        },
        collectTopMostCovered(node, selectedLeafSet) {
            const leavesUnderNode = this.getLeavesOfNode(node);
            const allLeavesCovered = leavesUnderNode.every((leafId) => selectedLeafSet.has(leafId));

            if (allLeavesCovered) {
                return { outputs: [node.id] };
            }

            // If leaf, include only if selected
            if (!node.children?.length) {
                return { outputs: selectedLeafSet.has(node.id) ? [node.id] : [] };
            }

            // Recurse into children
            const outputs = node.children.flatMap((child) => this.collectTopMostCovered(child, selectedLeafSet).outputs);
            return { outputs };
        },
        getLeavesOfNode(node) {
            if (!node.children?.length) return [node.id];
            return node.children.flatMap((child) => this.getLeavesOfNode(child));
        },
        getAllLeavesFromRoots(roots) {
            return [...new Set((roots || []).flatMap((root) => this.getLeavesOfNode(root)))];
        },
        coerceToNumber(value) {
            const num = Number(value);
            return Number.isNaN(num) ? value : num;
        },
    },
};
</script>
